'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getLocalGamificationMapState, getLocalLessonPreview, listLocalGamifiedLessons } from '@/app/actions/gamify-lab';
import { Loader2, ArrowLeft, Beaker } from 'lucide-react';
import LessonEngine from './LessonEngine';
import GamifiedTree, { GamificationState, LessonNode } from '@/components/gamification/gamified-tree';
import NextLessonCard from '@/components/gamification/next-lesson-card';
import { LearningAppHeader, LearningAppHeaderButton, LearningAppReaderControls, LearningAppSegmentTabs, LearningAppTierControls } from './LearningAppHeader';
import type { LessonTierVariant } from '@/lib/gamification/contracts/lesson-variants';
import type { LessonTierState } from '@/lib/gamification/services/lesson-tier-state';

interface LocalGamifiedLesson {
  department: string;
  targetAgent: string;
  baseLessonSlug: string;
  compiledTiers: string[];
}

interface SelectedLocalLesson {
  node: LessonNode;
  meta: LocalGamifiedLesson;
}

export default function GamifyLabClient({
  lessonTree,
  fontFamily = 'inter',
  fontSizeLevel = 1,
  onFontSizeChange,
  fullWidth = false,
  onFullWidthToggle,
}: {
  lessonTree: LessonNode[];
  fontFamily?: string;
  fontSizeLevel?: number;
  onFontSizeChange?: (level: number) => void;
  fullWidth?: boolean;
  onFullWidthToggle?: () => void;
}) {
  const [availableLessons, setAvailableLessons] = useState<LocalGamifiedLesson[]>([]);
  const [gamificationState, setGamificationState] = useState<GamificationState>({});
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState<SelectedLocalLesson | null>(null);
  const [focusedDir, setFocusedDir] = useState<LessonNode | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readerSelectedTier, setReaderSelectedTier] = useState<LessonTierVariant | null>(null);
  const [readerTierStates, setReaderTierStates] = useState<LessonTierState[]>([]);
  const [activeLabAreaId, setActiveLabAreaId] = useState<string | null>(null);

  const availableLessonsBySlug = useMemo(() => {
    return new Map(availableLessons.map((lesson) => [lesson.baseLessonSlug, lesson]));
  }, [availableLessons]);

  const localLessonTree = useMemo(() => {
    const filterTreeToLocal = (nodes: LessonNode[]): LessonNode[] => {
      return nodes
        .map((node) => {
          if (node.type === 'file') {
            const slug = node.id.replace('.md', '');
            return availableLessonsBySlug.has(slug) ? node : null;
          }

          const filteredChildren = filterTreeToLocal(node.children);
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...node,
            children: filteredChildren,
          };
        })
        .filter((node): node is LessonNode => node !== null);
    };

    return filterTreeToLocal(lessonTree);
  }, [availableLessonsBySlug, lessonTree]);

  const labAreaItems = useMemo(
    () => localLessonTree
      .slice()
      .sort((left, right) => left.title.localeCompare(right.title))
      .map((node) => ({
        id: node.id,
        label: node.title,
        locked: node.userHasRole === false,
      })),
    [localLessonTree]
  );

  useEffect(() => {
    if (labAreaItems.length === 0) {
      setActiveLabAreaId(null);
      return;
    }

    const hasActiveArea = activeLabAreaId
      ? labAreaItems.some((item) => item.id === activeLabAreaId)
      : false;

    if (!hasActiveArea) {
      setActiveLabAreaId(labAreaItems[0].id);
    }
  }, [labAreaItems, activeLabAreaId]);

  useEffect(() => {
    async function loadList() {
      setIsLoadingList(true);
      setIsLoadingMap(true);

      const listResult = await listLocalGamifiedLessons();
      if (listResult.success && listResult.lessons) {
        setAvailableLessons(listResult.lessons);

        const collectLessonSlugs = (nodes: LessonNode[]): string[] => {
          const slugs: string[] = [];
          for (const node of nodes) {
            if (node.type === 'file') {
              const slug = node.id.replace('.md', '');
              if (listResult.lessons?.some((lesson) => lesson.baseLessonSlug === slug)) {
                slugs.push(slug);
              }
              continue;
            }

            slugs.push(...collectLessonSlugs(node.children));
          }
          return slugs;
        };

        const labLessonSlugs = collectLessonSlugs(lessonTree);
        const mapResult = await getLocalGamificationMapState(labLessonSlugs);
        if (mapResult.success && mapResult.nodeStates) {
          setGamificationState(mapResult.nodeStates as GamificationState);
        } else {
          setGamificationState({});
          setError(mapResult.error || 'Failed to load local lesson map.');
        }
      } else {
        setAvailableLessons([]);
        setGamificationState({});
        setError(listResult.error || 'Failed to scan local gamified lessons.');
      }

      setIsLoadingList(false);
      setIsLoadingMap(false);
    }

    loadList();
  }, [lessonTree]);

  const loadLocalLessonMechanics = async (lessonSlug: string, selectedTier?: string) => {
    if (!selectedLesson) {
      return { success: false, message: 'No local lesson selected.' };
    }

    const res = await getLocalLessonPreview(
      lessonSlug,
      selectedLesson.meta.department,
      selectedLesson.meta.targetAgent,
      selectedTier
    );

    if (!res.success) {
      return { success: false, message: res.error || 'Failed to load local mechanics.' };
    }

    return {
      success: true,
      mechanics: res.mechanics,
    };
  };

  const handleSelectLesson = async (node: LessonNode) => {
    const lessonSlug = node.id.replace('.md', '');
    const meta = availableLessonsBySlug.get(lessonSlug);
    if (!meta) {
      setError(`Local lesson metadata not found for ${lessonSlug}.`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setFileContent(null);
    setReaderSelectedTier(null);
    setReaderTierStates([]);
    setSelectedLesson({ node, meta });

    const res = await getLocalLessonPreview(meta.baseLessonSlug, meta.department, meta.targetAgent);
    if (res.success && res.content) {
      setFileContent(res.content);
    } else {
      setError(res.error || 'Failed to load local lesson');
    }

    setIsLoading(false);
  };

  if (selectedLesson && fileContent) {
    return (
      <div className="flex flex-col h-full bg-background absolute inset-0 z-50 animate-in fade-in zoom-in-95 duration-200">
        <LearningAppHeader
          title={selectedLesson.node.title}
          badgeLabel="Local"
          badgeTone="amber"
          leading={(
            <LearningAppHeaderButton
              onClick={() => {
                setSelectedLesson(null);
                setFileContent(null);
              }}
              title="Back to lab browser"
              className="w-8 px-0"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </LearningAppHeaderButton>
          )}
          titleAside={(
            <LearningAppTierControls
              selectedTier={readerSelectedTier}
              tierStates={readerTierStates}
              onSelectTier={setReaderSelectedTier}
            />
          )}
          actions={(
            <LearningAppReaderControls
              onDecreaseFont={() => onFontSizeChange?.(Math.max(0, fontSizeLevel - 1))}
              onIncreaseFont={() => onFontSizeChange?.(Math.min(3, fontSizeLevel + 1))}
              onToggleWidth={() => onFullWidthToggle?.()}
              fullWidth={fullWidth}
            />
          )}
        />

        <ScrollArea className="flex-1 p-6 md:p-10 bg-background/95 backdrop-blur">
          <div className={`${fullWidth ? 'max-w-none' : 'max-w-3xl'} mx-auto w-full pb-20 relative`}>
            <div className="absolute top-0 right-0 p-4 border border-border/50 rounded-lg bg-muted/30 text-xs text-muted-foreground opacity-50">
              <Beaker className="w-4 h-4 mb-2 mx-auto" />
              Local mechanics.
              <br />
              Same lesson reader.
            </div>
            <LessonEngine
              content={fileContent}
              lessonSlug={selectedLesson.meta.baseLessonSlug}
              fontSizeLevel={fontSizeLevel}
              fontFamily={fontFamily}
              loadLessonMechanics={loadLocalLessonMechanics}
              persistLocalProgress={true}
              selectedTier={readerSelectedTier}
              onSelectedTierChange={setReaderSelectedTier}
              onTierStateChange={({ selectedTier, tierStates }) => {
                setReaderSelectedTier(selectedTier);
                setReaderTierStates(tierStates);
              }}
              showTierControlsInBody={false}
            />
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <LearningAppHeader
        title="Gamify Lab"
        badgeLabel="Local"
        badgeTone="amber"
        center={(
          <LearningAppSegmentTabs
            items={labAreaItems}
            activeId={activeLabAreaId}
            onSelect={setActiveLabAreaId}
          />
        )}
        leading={(
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-amber-500/20 bg-amber-500/10 text-amber-500">
            <Beaker className="w-3.5 h-3.5" />
          </div>
        )}
      />

      {isLoadingList || isLoadingMap || isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
          <p>
            {isLoading
              ? 'Loading Local Lesson...'
              : isLoadingMap
              ? 'Building Local Lesson Map...'
              : 'Scanning Local Tier Variants...'}
          </p>
        </div>
      ) : error ? (
        <div className="p-6 border border-destructive/50 bg-destructive/10 text-destructive rounded-lg max-w-xl mx-auto mt-10">
          <h3 className="font-bold mb-2">Lab Error</h3>
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)} className="mt-4 px-4 py-2 bg-destructive text-destructive-foreground rounded">
            Go Back
          </button>
        </div>
      ) : availableLessons.length === 0 ? (
        <p className="text-sm text-muted-foreground italic text-center mt-10">
          No local gamified lessons found. Generate canonical shadow files first.
        </p>
      ) : (
        <>
          {focusedDir && (
            <div className="w-full bg-background/95 backdrop-blur-md border border-border/30 rounded-xl z-30 shadow-sm px-4 md:px-6 py-4">
              <NextLessonCard
                gamificationState={gamificationState}
                focusedNode={focusedDir}
                onNodeClick={handleSelectLesson}
              />
            </div>
          )}

          <GamifiedTree
            lessonTree={localLessonTree}
            gamificationState={gamificationState}
            onNodeClick={handleSelectLesson}
            focusedNode={focusedDir}
            onFocusSelect={setFocusedDir}
            activeParentId={activeLabAreaId}
            onActiveParentChange={setActiveLabAreaId}
            showParentTabs={false}
          />
        </>
      )}
    </div>
  );
}
